// src/photos.jsx — Phase 17 Photo Storage (IndexedDB Blob)
//
// Exports: initPhotosDB, getPhoto, putPhoto, deletePhoto,
//          migratePhotosFromLocalStorage, photoBytes, exportPhotoData,
//          importPhotoData, restoredPhotos
//
// All photos live as Blob rows in Dexie `liftlog_photos` DB per
// DEC-photos-indexeddb-v2. Components never touch storage directly —
// always go through getPhoto(id) which returns URL.createObjectURL(blob).
// With sync on, photos also travel encrypted between devices (photo_sync.js):
// a photo this device doesn't have yet is downloaded the first time getPhoto
// asks for it. JSON backups can embed photos (exportPhotoData / importPhotoData).

import Dexie from 'dexie';
import { configurePhotoStore, fetchPhoto } from './photo_sync.js';

const PHOTOS_DB_NAME = 'liftlog_photos';
const PHOTOS_STORE = 'photos';
const LS_STATE_KEY = 'liftlog.v1'; // must match LS_KEY in src/store.jsx

let _db = null; // lazy-initialized Dexie instance

function initPhotosDB() {
  if (_db) return _db;
  // Dexie is bundled (package dependency). window.Dexie is honoured first so a
  // test harness can inject a fake; IndexedDB may be missing in odd contexts.
  const DexieCtor = (typeof window !== 'undefined' && window.Dexie) || Dexie;
  if (!DexieCtor || typeof indexedDB === 'undefined') {
    console.warn('[photos] IndexedDB unavailable — photos will not persist this session');
    return null;
  }
  _db = new DexieCtor(PHOTOS_DB_NAME);
  _db.version(1).stores({
    // Primary key: id. Indexed secondary: createdAt (for future sort queries).
    photos: 'id, createdAt',
  });
  return _db;
}

let _importing = Promise.resolve(); // photos being restored from a backup

async function getPhoto(id) {
  const db = initPhotosDB();
  if (!db || !id) return null;
  try {
    await _importing;
    const row = await db.table(PHOTOS_STORE).get(id);
    if (row && row.blob) return URL.createObjectURL(row.blob);
    // Not on this device: the synced copy may have it (taken on another device).
    const got = await fetchPhoto(id);
    if (!got) return null;
    return URL.createObjectURL(new Blob([got.bytes], { type: got.mime }));
  } catch (e) {
    console.error('[photos] getPhoto failed', id, e);
    return null;
  }
}

// The local image store photo_sync.js reads (uploads) and fills (downloads).
configurePhotoStore({
  has: async (id) => { const db = initPhotosDB(); if (!db) return false; try { return !!(await db.table(PHOTOS_STORE).get(id)); } catch (_) { return false; } },
  get: async (id) => {
    const db = initPhotosDB();
    if (!db) return null;
    const row = await db.table(PHOTOS_STORE).get(id);
    if (!row || !row.blob) return null;
    return { bytes: new Uint8Array(await row.blob.arrayBuffer()), mime: row.mime || row.blob.type || 'image/webp' };
  },
  put: async (id, { bytes, mime }) => {
    const db = initPhotosDB();
    if (!db) return;
    await db.table(PHOTOS_STORE).put({ id, createdAt: new Date().toISOString(), blob: new Blob([bytes], { type: mime }), mime, sourceId: 'sync' });
  },
});

// ——— Backups ———

/** Bytes of the photos stored on this device (of the given records). → { bytes, count } */
async function photoBytes(photos) {
  const db = initPhotosDB();
  let bytes = 0, count = 0;
  if (!db) return { bytes, count };
  for (const p of photos || []) {
    try { const row = await db.table(PHOTOS_STORE).get(p.id); if (row && row.blob) { bytes += row.blob.size; count++; } } catch (_) { /* skip */ }
  }
  return { bytes, count };
}
const blobToDataURL = (blob) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = () => reject(r.error);
  r.readAsDataURL(blob);
});
/** { <photoId>: 'data:image/webp;base64,…' } for a JSON backup (photos on this device only). */
async function exportPhotoData(photos) {
  const db = initPhotosDB();
  const out = {};
  if (!db) return out;
  for (const p of photos || []) {
    try { const row = await db.table(PHOTOS_STORE).get(p.id); if (row && row.blob) out[p.id] = await blobToDataURL(row.blob); } catch (_) { /* skip */ }
  }
  return out;
}
/** Write a backup's embedded photos into IndexedDB (getPhoto waits for it). */
function importPhotoData(photoData) {
  const db = initPhotosDB();
  if (!db || !photoData || typeof photoData !== 'object') return Promise.resolve(0);
  const job = (async () => {
    let n = 0;
    for (const [id, url] of Object.entries(photoData)) {
      if (typeof url !== 'string' || !/^data:image\/(webp|jpeg|png);base64,/.test(url)) continue;
      try {
        if (await db.table(PHOTOS_STORE).get(id)) continue;
        const blob = await (await fetch(url)).blob();
        await db.table(PHOTOS_STORE).put({ id, createdAt: new Date().toISOString(), blob, mime: blob.type, sourceId: 'backup' });
        n++;
      } catch (e) { console.warn('[photos] restore failed for', id, e); }
    }
    return n;
  })();
  _importing = job.catch(() => 0);
  return job;
}
/** Photo records after restoring a backup: this device's, plus the backup's that carry their image. */
function restoredPhotos(prevPhotos, raw) {
  const prev = Array.isArray(prevPhotos) ? prevPhotos : [];
  if (!raw || !Array.isArray(raw.photos) || !raw.photoData || typeof raw.photoData !== 'object') return prev;
  const have = new Set(prev.map((p) => p && p.id));
  const add = raw.photos
    .filter((p) => p && typeof p === 'object' && p.id && !have.has(p.id) && typeof raw.photoData[p.id] === 'string' && typeof p.date === 'string')
    .map((p) => ({ id: String(p.id), createdAt: p.createdAt || p.date, date: p.date, ...(p.pose ? { pose: String(p.pose) } : {}) }));
  return add.length ? [...prev, ...add] : prev;
}

// Resize + WebP encode a File/Blob via canvas. Returns { blob, mime }.
// EXIF is stripped as a side effect of re-encoding from canvas pixel data (PHOTO-03).
async function _encodeToWebP(file) {
  const bitmap = await createImageBitmap(file);
  const srcW = bitmap.width;
  const srcH = bitmap.height;
  const maxEdge = Math.max(srcW, srcH);
  const scale = maxEdge > 2048 ? 2048 / maxEdge : 1;
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  const canvas = document.createElement('canvas');
  canvas.width = dstW;
  canvas.height = dstH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, dstW, dstH);
  if (bitmap.close) bitmap.close();

  const blob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/webp', 0.85);
  });
  if (blob) return { blob, mime: 'image/webp' };

  console.warn('[photos] WebP toBlob returned null — falling back to JPEG');
  const jpegBlob = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
  });
  return { blob: jpegBlob, mime: 'image/jpeg' };
}

// putPhoto(file, meta) — real upload path.
// meta: { date: 'YYYY-MM-DD', pose: string }. Returns { id, createdAt, date, pose }.
async function putPhoto(file, meta) {
  const db = initPhotosDB();
  if (!db) throw new Error('Photos DB unavailable');
  const { blob, mime } = await _encodeToWebP(file);
  const createdAt = new Date().toISOString();
  const id = 'ph_' + createdAt.replace(/[^0-9]/g, '').slice(0, 14) + '_' +
             Math.random().toString(36).slice(2, 8);
  await db.table(PHOTOS_STORE).put({
    id, createdAt, blob, mime, sourceId: null,
  });
  return { id, createdAt, date: meta.date, pose: meta.pose };
}

async function deletePhoto(id) {
  const db = initPhotosDB();
  if (!db) return;
  try {
    await db.table(PHOTOS_STORE).delete(id);
  } catch (e) {
    console.error('[photos] deletePhoto failed', id, e);
  }
}

// Migrate v1.1 photos from localStorage base64/SVG-data-URI into IDB.
// Idempotent: safe to run on every boot. PHOTO-04.
async function migratePhotosFromLocalStorage() {
  const db = initPhotosDB();
  if (!db) return { migrated: 0, skipped: 0, errors: 0 };

  let stateStr = null;
  try { stateStr = localStorage.getItem(LS_STATE_KEY); } catch (_) { /* private mode */ }
  if (!stateStr) return { migrated: 0, skipped: 0, errors: 0 };

  let state;
  try { state = JSON.parse(stateStr); } catch (_) { return { migrated: 0, skipped: 0, errors: 0 }; }
  if (!state || !Array.isArray(state.photos) || state.photos.length === 0) {
    return { migrated: 0, skipped: 0, errors: 0 };
  }

  let migrated = 0, skipped = 0, errors = 0;
  const newPhotos = [];

  for (const entry of state.photos) {
    if (!entry.dataUrl) {
      skipped++;
      // Ensure createdAt exists on already-migrated rows too
      newPhotos.push(entry.createdAt ? entry : { ...entry, createdAt: entry.date || new Date().toISOString() });
      continue;
    }

    try {
      const res = await fetch(entry.dataUrl);
      const blob = await res.blob();
      const mime = blob.type || 'image/svg+xml';
      const id = entry.id || ('ph_migrated_' + Math.random().toString(36).slice(2, 10));
      const createdAt = entry.createdAt || entry.date || new Date().toISOString();
      await db.table(PHOTOS_STORE).put({ id, createdAt, blob, mime, sourceId: null });

      // Verify round-trip
      const check = await db.table(PHOTOS_STORE).get(id);
      if (!check || !check.blob || check.blob.size !== blob.size) {
        console.warn('[photos] migration verification failed for', id, '— keeping legacy entry');
        errors++;
        newPhotos.push(entry);
        continue;
      }

      migrated++;
      const { dataUrl: _drop, ...rest } = entry;
      newPhotos.push({ ...rest, id, createdAt });
    } catch (e) {
      console.error('[photos] migration error for entry', entry.id, e);
      errors++;
      newPhotos.push(entry);
    }
  }

  // Write back if anything changed
  if (migrated > 0) {
    try {
      localStorage.setItem(LS_STATE_KEY, JSON.stringify({ ...state, photos: newPhotos }));
    } catch (e) {
      console.error('[photos] localStorage write failed after migration', e);
    }
  }

  if (migrated > 0 || errors > 0) {
    console.log(`[photos] migration: ${migrated} migrated, ${skipped} already-migrated, ${errors} errors`);
  }
  return { migrated, skipped, errors };
}

export { initPhotosDB, getPhoto, putPhoto, deletePhoto, migratePhotosFromLocalStorage, photoBytes, exportPhotoData, importPhotoData, restoredPhotos };
